import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
// @ts-ignore
import { load } from "@cashfreepayments/cashfree-js";
import { BOOK_EVENT, CREATE_CART_PAYMENT_ORDER } from "../graphql/mutation";
import { useCart, CartItem } from "../context/CartContext";
import CustomSnackbar from "./Snackbar";
import { GET_EVENT_LIST, GET_PASS_LIST } from "../graphql/query";
import { getActivePassOffer, isEventOfferCurrentlyActive } from "../utils/offerPricing";

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

const fmt = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(amount);

/* ─── Single item row (Amazon-style) ─── */
const ItemRow: React.FC<{ item: CartItem; onRemove: () => void }> = ({
  item,
  onRemove,
}) => {
  const isPass = item.type === "pass";
  const hasDiscount = !!item.discount && item.discount > 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        py: 2.5,
        px: 0,
      }}
    >
      {/* ── Left icon block ── */}
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: "12px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isPass ? "#E8F5E9" : "#EDE7F6",
          overflow: "hidden",
        }}
      >
        {item.image ? (
          <Box
            component="img"
            src={item.image.startsWith("http") || item.image.startsWith("data:") ? item.image : `data:image/*;base64,${item.image}`}
            alt={item.name}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : isPass ? (
          <CardMembershipOutlinedIcon
            sx={{ fontSize: 24, color: "#2E7D32" }}
          />
        ) : (
          <CalendarMonthOutlinedIcon
            sx={{ fontSize: 24, color: "#5E35B1" }}
          />
        )}
      </Box>

      {/* ── Centre: name + meta ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Type label */}
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.6px",
            textTransform: "uppercase",
            color: isPass ? "#2E7D32" : "#5E35B1",
            mb: 0.25,
          }}
        >
          {isPass ? "Pass" : "Event"}
        </Typography>

        {/* Name */}
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 700,
            color: "#111827",
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.name}
        </Typography>

        {/* Subtitle */}
        {(item.styleType || item.instructorName) && (
          <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 0.25 }}>
            {[item.styleType, item.instructorName].filter(Boolean).join(" · ")}
          </Typography>
        )}

        {/* Date and Time */}
        {(item.date || item.time) && (
          <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 0.25 }}>
            {[item.date, item.time].filter(Boolean).join(" | ")}
          </Typography>
        )}

        {/* Discount badge */}
        {hasDiscount && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.4,
              mt: 0.75,
              px: 0.9,
              py: 0.3,
              borderRadius: "6px",
              backgroundColor: "#F0FBE8",
              border: "1px solid #C6ECA8",
            }}
          >
            <LocalOfferOutlinedIcon sx={{ fontSize: 11, color: "#5AA839" }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#5AA839" }}>
              {item.discount}% OFF
              {item.discountLabel ? ` · ${item.discountLabel}` : ""}
            </Typography>
          </Box>
        )}

        {/* Delete link — Amazon style */}
        <Box
          component="span"
          onClick={onRemove}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.4,
            mt: 1,
            cursor: "pointer",
            color: "#9CA3AF",
            fontSize: 12,
            fontWeight: 500,
            userSelect: "none",
            transition: "color 0.15s",
            "&:hover": { color: "#EF4444" },
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          Remove
        </Box>
      </Box>

      {/* ── Right: price ── */}
      <Box sx={{ textAlign: "right", flexShrink: 0, minHeight: 40, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
        {hasDiscount && (
          <Typography
            sx={{
              fontSize: 11,
              color: "#9CA3AF",
              textDecoration: "line-through",
              lineHeight: 1,
              mb: 0.25,
            }}
          >
            {fmt(item.originalPrice)}
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 800,
            color: hasDiscount ? "#5AA839" : "#111827",
            lineHeight: 1.2,
          }}
        >
          {fmt(item.finalPrice)}
        </Typography>
      </Box>
    </Box>
  );
};

/* ─── Main drawer ─── */
const CartDrawer: React.FC<CartDrawerProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const {
    items,
    removeItem,
    clearCart,
    replaceItems,
    totalCount,
    subtotal,
    totalDiscount,
    grandTotal,
  } = useCart();

  const [checkingOut, setCheckingOut] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error" | "info">(
    "success"
  );

  const passIdsInCart = useMemo(
    () => items.filter((item) => item.type === "pass").map((item) => item.id),
    [items]
  );
  const eventIdsInCart = useMemo(
    () => items.filter((item) => item.type === "event").map((item) => item.id),
    [items]
  );

  const { data: passListData } = useQuery(GET_PASS_LIST, {
    variables: {
      page: 1,
      perPage: 1000,
      search: "",
      styleType: "",
      instructorName: "",
      status: "Active",
    },
    fetchPolicy: "network-only",
    skip: !open || passIdsInCart.length === 0,
  });

  const { data: eventListData } = useQuery(GET_EVENT_LIST, {
    variables: {
      page: 1,
      perPage: 1000,
      search: "",
    },
    fetchPolicy: "network-only",
    skip: !open || eventIdsInCart.length === 0,
  });

  const [createCartPaymentOrder] = useMutation(CREATE_CART_PAYMENT_ORDER);
  const [bookEvent] = useMutation(BOOK_EVENT);

  const isFreeEventCart = useMemo(
    () =>
      items.length > 0 &&
      grandTotal <= 0 &&
      items.every((item) => item.type === "event"),
    [grandTotal, items]
  );

  useEffect(() => {
    if (!open) return;
    if (passIdsInCart.length > 0 && !passListData?.getPassList?.data) return;
    if (eventIdsInCart.length > 0 && !eventListData?.getEventList?.data) return;

    const passMap = new Map(
      (passListData?.getPassList?.data ?? []).map((pass: any) => [pass.id, pass])
    );
    const eventMap = new Map(
      (eventListData?.getEventList?.data ?? []).map((event: any) => [event.id, event])
    );

    let changed = false;
    const nextItems = items.map((item) => {
      if (item.type === "pass") {
        const pass = passMap.get(item.id) as any;
        if (!pass) return item;

        const activeOffer = getActivePassOffer(pass.offers);
        const nextFinalPrice = activeOffer
          ? Number((pass.passCost - (pass.passCost * activeOffer.discount) / 100).toFixed(2))
          : pass.passCost || 0;
        const nextItem = {
          ...item,
          originalPrice: pass.passCost || 0,
          finalPrice: nextFinalPrice,
          discount: activeOffer?.discount,
          discountLabel: activeOffer?.offerName,
          discountCode: activeOffer?.offerCode,
        };

        if (JSON.stringify(nextItem) !== JSON.stringify(item)) {
          changed = true;
        }
        return nextItem;
      }

      const event = eventMap.get(item.id) as any;
      if (!event) return item;

      const hasActiveOffer = isEventOfferCurrentlyActive(event);
      const nextFinalPrice = hasActiveOffer ? event.offerPrice || 0 : event.standardPrice || 0;
      const nextDiscount =
        hasActiveOffer && event.standardPrice > 0
          ? Math.round(((event.standardPrice - event.offerPrice) / event.standardPrice) * 100)
          : undefined;
      const nextItem = {
        ...item,
        originalPrice: event.standardPrice || 0,
        finalPrice: nextFinalPrice,
        discount: nextDiscount,
        discountLabel: hasActiveOffer ? event.promoCode : undefined,
        discountCode: hasActiveOffer ? event.promoCode : undefined,
      };

      if (JSON.stringify(nextItem) !== JSON.stringify(item)) {
        changed = true;
      }
      return nextItem;
    });

    if (changed) {
      replaceItems(nextItems);
    }
  }, [
    eventIdsInCart.length,
    eventListData,
    items,
    open,
    passIdsInCart.length,
    passListData,
    replaceItems,
  ]);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setCheckingOut(true);

    try {
      if (isFreeEventCart) {
        for (const item of items) {
          const { data } = await bookEvent({ variables: { eventId: item.id } });
          if (!data?.bookEvent?.success) {
            throw new Error(data?.bookEvent?.message || `Failed to book ${item.name}`);
          }
        }
        clearCart();
        setSnackbarMessage("Free event booked successfully.");
        setSnackbarType("success");
        setSnackbarOpen(true);
        onClose();
        return;
      }

      const { data } = await createCartPaymentOrder({
        variables: {
          items: items.map((i) => ({
            id: i.id,
            type: i.type,
            name: i.name,
            finalPrice: i.finalPrice,
            discountCode: i.discountCode,
          })),
          returnUrl: `${window.location.origin}/payment/callback?source=cart&order_id={order_id}`,
        },
      });

      if (!data?.createCartPaymentOrder?.success) {
        setSnackbarMessage(data?.createCartPaymentOrder?.message || "Failed to initialize checkout.");
        setSnackbarType("error");
        setSnackbarOpen(true);
        setCheckingOut(false);
        return;
      }

      const { paymentSessionId, cfMode } = data.createCartPaymentOrder;
      const cashfree = await load({ mode: cfMode || "sandbox" });

      if (!cashfree) {
        throw new Error("Cashfree SDK failed to load");
      }

      await cashfree.checkout({
        paymentSessionId,
        redirectTarget: "_self",
      });
    } catch (err) {
      console.error(err);
      setSnackbarMessage(
        err instanceof Error ? err.message : "Failed to initialize checkout."
      );
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: 440 },
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#fff",
          },
        }}
      >
        {/* ══ HEADER ══ */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid #F3F4F6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.2}>
            <ShoppingCartOutlinedIcon sx={{ color: "#6CBC44", fontSize: 22 }} />
            <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#111827" }}>
              My Cart
            </Typography>
            {totalCount > 0 && (
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  backgroundColor: "#6CBC44",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#fff" }}>
                  {totalCount}
                </Typography>
              </Box>
            )}
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              width: 32,
              height: 32,
              "&:hover": { backgroundColor: "#F9FAFB", color: "#374151" },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* ══ BODY ══ */}
        {totalCount === 0 ? (
          /* ── Empty state ── */
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              px: 4,
              backgroundColor: "#FAFAFA",
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "#F3F4F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <ShoppingCartOutlinedIcon sx={{ fontSize: 36, color: "#D1D5DB" }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#374151" }}>
              Your cart is empty
            </Typography>
            <Typography
              sx={{ fontSize: 13, color: "#9CA3AF", textAlign: "center", lineHeight: 1.6 }}
            >
              Add passes or events to your cart and they'll appear here.
            </Typography>
            <Box display="flex" gap={1.5} mt={1.5}>
              <Button
                variant="outlined"
                onClick={() => { navigate("/passes"); onClose(); }}
                sx={{
                  borderColor: "#6CBC44",
                  color: "#6CBC44",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  px: 2.5,
                  fontSize: 13,
                  "&:hover": { borderColor: "#5AA839", backgroundColor: "#F0FBE8" },
                }}
              >
                Browse Passes
              </Button>
              <Button
                variant="outlined"
                onClick={() => { navigate("/events"); onClose(); }}
                sx={{
                  borderColor: "#5E35B1",
                  color: "#5E35B1",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  px: 2.5,
                  fontSize: 13,
                  "&:hover": { borderColor: "#4527A0", backgroundColor: "#EDE7F6" },
                }}
              >
                Browse Events
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {/* ── Item list (scrollable) ── */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 3,
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  background: "#E5E7EB",
                  borderRadius: 4,
                },
              }}
            >
              {items.map((item, idx) => (
                <Box key={item.id}>
                  <ItemRow item={item} onRemove={() => removeItem(item.id)} />
                  {idx < items.length - 1 && (
                    <Divider sx={{ borderColor: "#F3F4F6" }} />
                  )}
                </Box>
              ))}
            </Box>

            {/* ── Order summary + checkout (sticky bottom) ── */}
            <Box
              sx={{
                flexShrink: 0,
                borderTop: "2px solid #F3F4F6",
                backgroundColor: "#FAFAFA",
              }}
            >
              {/* Order Summary */}
              <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: "#111827",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: 1.5,
                  }}
                >
                  Order Summary
                </Typography>

                {/* Per-item lines */}
                <Box display="flex" flexDirection="column" gap={0.75} mb={1.25}>
                  {items.map((item) => (
                    <Box
                      key={item.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "#6B7280",
                          maxWidth: "65%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                        {fmt(item.finalPrice)}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ borderColor: "#E5E7EB", mb: 1.25 }} />

                {/* Subtotal */}
                <Box display="flex" justifyContent="space-between" mb={0.75}>
                  <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                    Subtotal ({totalCount} {totalCount === 1 ? "item" : "items"})
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                    {fmt(subtotal)}
                  </Typography>
                </Box>

                {/* Savings */}
                {totalDiscount > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={0.75}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#5AA839" }}>
                      Discount savings
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#5AA839" }}>
                      − {fmt(totalDiscount)}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ borderColor: "#E5E7EB", my: 1.25 }} />

                {/* Grand total */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>
                    Total
                  </Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#111827" }}>
                    {fmt(grandTotal)}
                  </Typography>
                </Box>
              </Box>

              {/* Pay button */}
              <Box sx={{ px: 3, pt: 1.5, pb: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={checkingOut}
                  onClick={handleCheckout}
                  endIcon={!checkingOut && <ArrowForwardIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    backgroundColor: "#6CBC44",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 15,
                    borderRadius: "12px",
                    py: 1.6,
                    boxShadow: "0px 4px 20px rgba(108, 188, 68, 0.4)",
                    "&:hover": {
                      backgroundColor: "#5AA839",
                      boxShadow: "0px 6px 24px rgba(108, 188, 68, 0.5)",
                    },
                    "&:disabled": { backgroundColor: "#C5E1A5", color: "#fff" },
                  }}
                >
                  {checkingOut ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                      Processing payment…
                    </Box>
                  ) : isFreeEventCart ? (
                    "Book Free Event"
                  ) : (
                    `Pay ${fmt(grandTotal)}`
                  )}
                </Button>
              </Box>

              {/* Clear cart */}
              <Box sx={{ textAlign: "center", pb: 2.5 }}>
                <Button
                  onClick={clearCart}
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#9CA3AF",
                    borderColor: "#E5E7EB",
                    borderRadius: "100px",
                    px: 2,
                    py: 0.5,
                    textTransform: "none",
                    "&:hover": {
                      color: "#EF4444",
                      borderColor: "#FCA5A5",
                      backgroundColor: "#FEF2F2",
                    },
                  }}
                >
                  Clear cart
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Drawer>

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default CartDrawer;
